import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Download, Mail, FileText, Upload, Plus, Trash2 } from "lucide-react";
import { generateInvoicePDF } from "@/utils/invoicePdfExport";
import { InvoiceHistory } from "@/components/billing/InvoiceHistory";
import { AppointmentsTable } from "@/components/billing/AppointmentsTable";
import { BillingAnalytics } from "@/components/billing/BillingAnalytics";
import { ProductOrdersTable } from "@/components/billing/ProductOrdersTable";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";

interface Patient {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  date_of_birth: string | null;
}

interface Service {
  id: string;
  name: string;
  price: number;
  description: string | null;
  type?: "service" | "product" | "event" | "accompaniment";
}

interface PractitionerInfo {
  id: string;
  business_name: string;
  practitioner_name: string;
  address: string;
  phone: string;
  email: string;
  siret: string | null;
}

interface InvoiceItem {
  serviceId: string;
  name: string;
  description: string | null;
  unitPrice: number;
  quantity: number;
  discount: number; // Discount percentage (0, 5, 10, 15, etc.)
  total: number;
  type: string;
}

const Billing = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [practitionerInfo, setPractitionerInfo] = useState<PractitionerInfo | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<string>("");
  const [selectedServiceId, setSelectedServiceId] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  const [itemQuantity, setItemQuantity] = useState<number>(1);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      // Load patients
      const { data: patientsData, error: patientsError } = await supabase
        .from("patients")
        .select("*")
        .eq("user_id", user.id)
        .order("name");

      if (patientsError) throw patientsError;
      setPatients(patientsData || []);

      // Load practitioner info
      const { data: practitionerData, error: practitionerError } = await (supabase as any)
        .from("practitioner_info")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (practitionerError) throw practitionerError;

      // If no practitioner info exists, create it with default data
      if (!practitionerData) {
        const { data: newPractitioner, error: insertError } = await (supabase as any)
          .from("practitioner_info")
          .insert({
            user_id: user.id,
            business_name: "REGARD DE NATURE - Naturopathie pour petits & grands",
            practitioner_name: "Laura ELIE",
            phone: "06 37 03 18 66",
            address: "31 rue de l'ancien petit fossé, 31450 Montesquieu-Lauragais",
            email: user.email || "",
            siret: "104 142 344 00019",
          })
          .select()
          .single();

        if (insertError) throw insertError;
        setPractitionerInfo(newPractitioner);
      } else {
        setPractitionerInfo(practitionerData);
      }

      // Load services
      const { data: servicesData, error: servicesError } = await (supabase as any)
        .from("services")
        .select("*")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .order("name");

      if (servicesError) throw servicesError;

      // Load active products
      const { data: productsData, error: productsError } = await (supabase as any)
        .from("products")
        .select("id, title, price, short_description")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .order("title");

      if (productsError) throw productsError;

      // Load active events
      const { data: eventsData, error: eventsError } = await (supabase as any)
        .from("events")
        .select("id, title, price, short_description")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .order("title");

      if (eventsError) throw eventsError;

      // Load active accompaniments
      const { data: accompanimentsData, error: accompanimentsError } = await (supabase as any)
        .from("accompaniments")
        .select("id, title, price, short_description")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .order("title");

      if (accompanimentsError) throw accompanimentsError;

      // Combine all services, products, events, and accompaniments
      const allServices: Service[] = [];

      // If no services exist, create default services
      if (!servicesData || servicesData.length === 0) {
        const defaultServices = [
          {
            user_id: user.id,
            name: "Naturopathie Adultes",
            price: 60,
            description: "Consultation de naturopathie pour adultes",
            is_active: true,
          },
          {
            user_id: user.id,
            name: "Naturopathie Enfants & Ados",
            price: 55,
            description: "Consultation de naturopathie pour enfants et adolescents",
            is_active: true,
          },
          {
            user_id: user.id,
            name: "Gestion du Stress & Sommeil",
            price: 60,
            description: "Accompagnement pour la gestion du stress et du sommeil",
            is_active: true,
          },
          {
            user_id: user.id,
            name: "Approche Énergétique",
            price: 55,
            description: "Séance d'approche énergétique",
            is_active: true,
          },
        ];

        const { data: newServices, error: insertServicesError } = await (supabase as any)
          .from("services")
          .insert(defaultServices)
          .select();

        if (insertServicesError) throw insertServicesError;

        newServices?.forEach((service: any) => {
          allServices.push({
            ...service,
            type: "service",
          });
        });
      } else {
        servicesData.forEach((service: any) => {
          allServices.push({
            ...service,
            type: "service",
          });
        });
      }

      // Add products
      productsData?.forEach((product: any) => {
        allServices.push({
          id: product.id,
          name: product.title,
          price: product.price,
          description: product.short_description,
          type: "product",
        });
      });

      // Add events
      eventsData?.forEach((event: any) => {
        allServices.push({
          id: event.id,
          name: event.title,
          price: event.price,
          description: event.short_description,
          type: "event",
        });
      });

      // Add accompaniments
      accompanimentsData?.forEach((accompaniment: any) => {
        allServices.push({
          id: accompaniment.id,
          name: accompaniment.title,
          price: accompaniment.price,
          description: accompaniment.short_description,
          type: "accompaniment",
        });
      });

      setServices(allServices);
    } catch (error: any) {
      console.error("Erreur chargement données:", error);
      toast.error("Erreur lors du chargement des données");
    }
  };

  const handleSignatureUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatureDataUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const addItemToInvoice = () => {
    if (!selectedServiceId) {
      toast.error("Veuillez sélectionner un service/produit");
      return;
    }

    const service = services.find((s) => s.id === selectedServiceId);
    if (!service) return;

    // Check if item already exists
    const existingItemIndex = invoiceItems.findIndex((item) => item.serviceId === selectedServiceId);

    if (existingItemIndex >= 0) {
      // Update quantity
      const updatedItems = [...invoiceItems];
      updatedItems[existingItemIndex].quantity += itemQuantity;
      const discountMultiplier = 1 - (updatedItems[existingItemIndex].discount / 100);
      updatedItems[existingItemIndex].total = updatedItems[existingItemIndex].unitPrice * updatedItems[existingItemIndex].quantity * discountMultiplier;
      setInvoiceItems(updatedItems);
    } else {
      // Add new item
      const newItem: InvoiceItem = {
        serviceId: service.id,
        name: service.name,
        description: service.description,
        unitPrice: service.price,
        quantity: itemQuantity,
        discount: 0,
        total: service.price * itemQuantity,
        type: service.type || "service",
      };
      setInvoiceItems([...invoiceItems, newItem]);
    }

    // Reset selection
    setSelectedServiceId("");
    setItemQuantity(1);
    toast.success("Article ajouté à la facture");
  };

  const removeItemFromInvoice = (index: number) => {
    const updatedItems = invoiceItems.filter((_, i) => i !== index);
    setInvoiceItems(updatedItems);
  };

  const updateItemQuantity = (index: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    const updatedItems = [...invoiceItems];
    updatedItems[index].quantity = newQuantity;
    const discountMultiplier = 1 - (updatedItems[index].discount / 100);
    updatedItems[index].total = updatedItems[index].unitPrice * newQuantity * discountMultiplier;
    setInvoiceItems(updatedItems);
  };

  const updateItemDiscount = (index: number, newDiscount: number) => {
    const updatedItems = [...invoiceItems];
    updatedItems[index].discount = newDiscount;
    const discountMultiplier = 1 - (newDiscount / 100);
    updatedItems[index].total = updatedItems[index].unitPrice * updatedItems[index].quantity * discountMultiplier;
    setInvoiceItems(updatedItems);
  };

  const calculateTotal = () => {
    return invoiceItems.reduce((sum, item) => sum + item.total, 0);
  };

  const generateInvoice = async (sendEmail: boolean = false) => {
    if (!selectedPatientId) {
      toast.error("Veuillez sélectionner un consultant");
      return;
    }

    if (invoiceItems.length === 0) {
      toast.error("Veuillez ajouter au moins un article à la facture");
      return;
    }

    if (!practitionerInfo) {
      toast.error("Veuillez d'abord configurer vos informations de praticien");
      return;
    }

    setLoading(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const patient = patients.find((p) => p.id === selectedPatientId);

      if (!patient) {
        throw new Error("Patient introuvable");
      }

      // Generate invoice number using database function
      const { data: invoiceNumber, error: functionError } = await (supabase as any).rpc("generate_invoice_number");

      if (functionError) throw functionError;

      const totalAmount = calculateTotal();

      // Create invoice record with items
      const invoiceItemsData = invoiceItems.map((item) => ({
        service_id: item.serviceId,
        name: item.name,
        description: item.description,
        unit_price: item.unitPrice,
        quantity: item.quantity,
        total: item.total,
        type: item.type,
      }));

      const { data: invoice, error: invoiceError } = await (supabase as any)
        .from("invoices")
        .insert({
          user_id: user.id,
          patient_id: selectedPatientId,
          // IMPORTANT: invoices.service_id référence la table services → ne jamais y mettre un produit/événement/accompagnement.
          service_id: invoiceItems.find((it) => it.type === "service")?.serviceId ?? null,
          invoice_number: invoiceNumber,
          invoice_date: invoiceDate,
          amount: totalAmount,
          signature_url: signatureDataUrl || null,
          invoice_items: invoiceItemsData,
        })
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // Generate PDF with multiple items
      const invoiceData = {
        ...invoice,
        // IMPORTANT: PostgREST renvoie souvent les numeric en string → on force un number
        amount: totalAmount,
        patient,
        items: invoiceItems,
        practitioner: practitionerInfo,
      };

      const pdfBytes = await generateInvoicePDF(invoiceData);

      if (sendEmail) {
        if (!patient.email) {
          toast.error("Ce consultant n'a pas d'email renseigné");
        } else {
          const { data, error: emailError } = await supabase.functions.invoke("send-invoice", {
            body: {
              email: patient.email,
              recipientName: patient.name,
              invoiceNumber,
              total: totalAmount,
              pdfBuffer: Array.from(pdfBytes),
              items: invoiceItems.map((it) => ({
                name: it.name,
                quantity: Number(it.quantity),
                unitPrice: Number(it.unitPrice),
                total: Number(it.total),
              })),
            },
          });

          if (emailError) {
            console.error("Erreur send-invoice:", emailError);
            throw emailError;
          }

          if ((data as any)?.error) {
            console.error("send-invoice response error:", data);
            throw new Error((data as any).error);
          }

          toast.success("Facture envoyée par email");
        }
      }

      toast.success("Facture générée avec succès");

      // Reset form
      setSelectedPatientId("");
      setInvoiceItems([]);
      setSignatureDataUrl("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: any) {
      console.error("Erreur génération facture:", error);
      toast.error("Erreur lors de la génération de la facture", {
        description: error?.message || "Détail indisponible",
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedPatient = patients.find((p) => p.id === selectedPatientId);

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "service":
        return "Service";
      case "product":
        return "Produit";
      case "event":
        return "Événement";
      case "accompaniment":
        return "Accompagnement";
      default:
        return type;
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Facturation"
        subtitle="Gérez vos factures, rendez-vous et commandes"
        icon={<FileText className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="secondary"
      />

      {/* Tabs Navigation */}
        <Tabs defaultValue="create" className="space-y-6">
          {/* Mobile: horizontal scroll tabs */}
          <div className="overflow-x-auto -mx-4 px-4 pb-2 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-max sm:grid sm:w-full sm:grid-cols-5 gap-1 min-w-full sm:min-w-0">
              <TabsTrigger value="create" className="text-[10px] sm:text-xs px-3 sm:px-4 whitespace-nowrap">
                Créer
              </TabsTrigger>
              <TabsTrigger value="history" className="text-[10px] sm:text-xs px-3 sm:px-4 whitespace-nowrap">
                Historique
              </TabsTrigger>
              <TabsTrigger value="appointments" className="text-[10px] sm:text-xs px-3 sm:px-4 whitespace-nowrap">
                Factures
              </TabsTrigger>
              <TabsTrigger value="products" className="text-[10px] sm:text-xs px-3 sm:px-4 whitespace-nowrap">
                Commandes
              </TabsTrigger>
              <TabsTrigger value="analytics" className="text-[10px] sm:text-xs px-3 sm:px-4 whitespace-nowrap">
                Analytique
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Create Invoice Tab */}
          <TabsContent value="create">
            <Card className="shadow-card bg-gradient-card border-0">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-2xl md:text-3xl font-playfair text-primary flex items-center gap-2">
                  <FileText className="w-6 h-6" />
                  Facturation Multi-lignes
                </CardTitle>
                <CardDescription>Générez vos factures avec plusieurs articles</CardDescription>
              </CardHeader>

              <CardContent className="space-y-8 pt-6">
                {/* Practitioner Info Alert */}
                {!practitionerInfo && (
                  <div className="p-4 bg-destructive-light border border-destructive rounded-lg">
                    <p className="text-sm text-destructive-foreground">
                      ⚠️ Veuillez d'abord configurer vos informations de praticien dans les paramètres du compte
                    </p>
                  </div>
                )}

                {/* Patient and Date Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Patient Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="patient">Consultant *</Label>
                    <Select value={selectedPatientId} onValueChange={setSelectedPatientId}>
                      <SelectTrigger id="patient">
                        <SelectValue placeholder="Sélectionner un consultant" />
                      </SelectTrigger>
                      <SelectContent>
                        {patients.map((patient) => (
                          <SelectItem key={patient.id} value={patient.id}>
                            {patient.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Invoice Date */}
                  <div className="space-y-2">
                    <Label htmlFor="invoiceDate">Date de facturation *</Label>
                    <Input
                      id="invoiceDate"
                      type="date"
                      value={invoiceDate}
                      onChange={(e) => setInvoiceDate(e.target.value)}
                    />
                  </div>
                </div>

                {/* Add Items Section */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold">Ajouter des articles</Label>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    {/* Service Selection */}
                    <div className="md:col-span-7 space-y-2">
                      <Label htmlFor="service">Service / Accompagnement / Produit / Événement</Label>
                      <Select value={selectedServiceId} onValueChange={setSelectedServiceId}>
                        <SelectTrigger id="service">
                          <SelectValue placeholder="Sélectionner un article" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.filter((s) => s.type === "service").length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">Services</div>
                              {services
                                .filter((s) => s.type === "service")
                                .map((service) => (
                                  <SelectItem key={service.id} value={service.id}>
                                    {service.name} - {service.price}€
                                  </SelectItem>
                                ))}
                            </>
                          )}
                          {services.filter((s) => s.type === "accompaniment").length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">Accompagnements</div>
                              {services
                                .filter((s) => s.type === "accompaniment")
                                .map((service) => (
                                  <SelectItem key={service.id} value={service.id}>
                                    {service.name} - {service.price}€
                                  </SelectItem>
                                ))}
                            </>
                          )}
                          {services.filter((s) => s.type === "event").length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                                Événements & Stages
                              </div>
                              {services
                                .filter((s) => s.type === "event")
                                .map((service) => (
                                  <SelectItem key={service.id} value={service.id}>
                                    {service.name} - {service.price}€
                                  </SelectItem>
                                ))}
                            </>
                          )}
                          {services.filter((s) => s.type === "product").length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">Produits</div>
                              {services
                                .filter((s) => s.type === "product")
                                .map((service) => (
                                  <SelectItem key={service.id} value={service.id}>
                                    {service.name} - {service.price}€
                                  </SelectItem>
                                ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Quantity */}
                    <div className="md:col-span-2 space-y-2">
                      <Label htmlFor="quantity">Quantité</Label>
                      <Input
                        id="quantity"
                        type="number"
                        min="1"
                        value={itemQuantity}
                        onChange={(e) => setItemQuantity(parseInt(e.target.value) || 1)}
                      />
                    </div>

                    {/* Add Button */}
                    <div className="md:col-span-3">
                      <Button onClick={addItemToInvoice} className="w-full gap-2">
                        <Plus className="w-4 h-4" />
                        Ajouter
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Invoice Items Table */}
                {invoiceItems.length > 0 && (
                  <div className="space-y-4">
                    <Label className="text-lg font-semibold">Articles de la facture</Label>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead>Désignation</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Prix unitaire</TableHead>
                            <TableHead className="text-center w-24">Quantité</TableHead>
                            <TableHead className="text-center w-28">Remise</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                            <TableHead className="w-12"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invoiceItems.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{item.name}</p>
                                  {item.description && (
                                    <p className="text-xs text-muted-foreground">{item.description}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                                  {getTypeLabel(item.type)}
                                </span>
                              </TableCell>
                              <TableCell className="text-right">{item.unitPrice.toFixed(2)} €</TableCell>
                              <TableCell className="text-center">
                                <Input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => updateItemQuantity(index, parseInt(e.target.value) || 1)}
                                  className="w-20 text-center mx-auto"
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                <Select
                                  value={item.discount.toString()}
                                  onValueChange={(value) => updateItemDiscount(index, parseInt(value))}
                                >
                                  <SelectTrigger className="w-24 mx-auto">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50].map((discount) => (
                                      <SelectItem key={discount} value={discount.toString()}>
                                        {discount}%
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="text-right font-medium">{item.total.toFixed(2)} €</TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeItemFromInvoice(index)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {/* Total Row */}
                          <TableRow className="bg-primary/5 font-bold">
                            <TableCell colSpan={5} className="text-right text-lg">
                              TOTAL TTC
                            </TableCell>
                            <TableCell className="text-right text-lg text-primary">
                              {calculateTotal().toFixed(2)} €
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Signature Upload */}
                <div className="space-y-2">
                  <Label htmlFor="signature">Signature électronique</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="signature"
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleSignatureUpload}
                      className="flex-1"
                    />
                    <Button type="button" variant="outline" size="icon" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="w-4 h-4" />
                    </Button>
                  </div>
                  {signatureDataUrl && (
                    <div className="mt-2 p-2 border border-border rounded-lg bg-background">
                      <img src={signatureDataUrl} alt="Signature" className="h-16 object-contain" />
                    </div>
                  )}
                </div>

                {/* Preview Section */}
                {selectedPatient && invoiceItems.length > 0 && (
                  <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                    <h4 className="font-semibold text-primary">Aperçu de la facture</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Consultant:</span>
                        <p className="font-medium">{selectedPatient.name}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Nombre d'articles:</span>
                        <p className="font-medium">{invoiceItems.length}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Date:</span>
                        <p className="font-medium">{new Date(invoiceDate).toLocaleDateString("fr-FR")}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Montant total:</span>
                        <p className="font-medium text-primary text-lg">{calculateTotal().toFixed(2)} €</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button
                    onClick={() => generateInvoice(false)}
                    disabled={loading || !selectedPatientId || invoiceItems.length === 0}
                    className="flex-1 gap-2"
                  >
                    <Download className="w-4 h-4" />
                    {loading ? "Génération..." : "Générer PDF"}
                  </Button>
                  <Button
                    onClick={() => generateInvoice(true)}
                    disabled={loading || !selectedPatientId || invoiceItems.length === 0 || !selectedPatient?.email}
                    variant="outline"
                    className="flex-1 gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Générer et envoyer par email
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            {practitionerInfo && <InvoiceHistory practitionerInfo={practitionerInfo} />}
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <AppointmentsTable />
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <ProductOrdersTable />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <BillingAnalytics />
          </TabsContent>
        </Tabs>
    </PageContainer>
  );
};

export default Billing;
